<script setup lang="ts">
import type { FilterChangePayload, PageFilter } from '@rialtic/types'
import { useVirtualizer } from '@tanstack/vue-virtual'
import { useArrayFilter, useArrayMap, useDebounceFn } from '@vueuse/core'
import { computed, ref, useTemplateRef, watch } from '#imports'
import type { FilterItemWithState } from '../../composables/filters'
import { useUrlParamsState } from '../../composables/urlParamsState'
import useSimpleSearch from '../../composables/useSimpleSearch'
import Button from '../Button.vue'
import FloatingUi from '../FloatingUi.vue'
import ListItemCheckbox from '../list/ItemCheckbox.vue'
import Search from '../Search.vue'
import FilterChip from './Chip.vue'

interface Props {
  filter: PageFilter
  search?: boolean
  selectAllDisabled?: boolean
  clearAllDisabled?: boolean
}

const props = defineProps<Props>()

const { setUrlParams, getUrlParams, deleteUrlParam } = useUrlParamsState()

const emit = defineEmits([
  'filter-all',
  'filter-change',
  'filter-clear',
  'error',
])

const activator = ref(null)
const open = ref(false)
const searchInput = ref('')
const inputText = ref('')

// Keep track of all user-added items (even when unchecked)
const userAddedItemsSet = ref<Set<string>>(new Set())

// TODO: remove any cast - was `(item as any).display_name` in template
const predefinedItems = computed(() => (props.filter.items || []) as any[])

// Get user-added items from URL params
const urlParams = computed(() => getUrlParams(props.filter.name))
const predefinedValues = predefinedItems.value.map((item) => item.value)
const userAddedItems = computed(() => {
  // Get all user-added items (both active and inactive)
  const allUserAdded = Array.from(userAddedItemsSet.value)

  return allUserAdded.map((value) => ({
    value,
    label: value,
    active: urlParams.value.includes(value),
    isUserAdded: true,
  }))
})

// Combine predefined and user-added items
const allItems = computed(() => [
  ...predefinedItems.value.map((item) => ({
    ...item,
    active: urlParams.value.includes(item.value.toString()),
    isUserAdded: false,
  })),
  ...userAddedItems.value,
])

const activeFilterLabels = useArrayMap(
  useArrayFilter(allItems, ({ active }) => active),
  ({ label }) => label,
)

const filteredItems = useSimpleSearch<
  FilterItemWithState & { isUserAdded?: boolean }
>(allItems, searchInput, ['label', 'value'])

const setOpen = (isOpen = false) => {
  open.value = isOpen
}

const removeUrlParamFilter = (paramName: string, paramValue: string) => {
  const params = getUrlParams(paramName)
  const revisedParams = params.filter((param) => param !== paramValue)

  if (!revisedParams.length) {
    deleteUrlParam(paramName)
  } else {
    setUrlParams(paramName, revisedParams)
  }
}

const filterChange = (item: FilterItemWithState, name: string) => {
  // Update URL params immediately for instant UI feedback
  if (item.active) {
    removeUrlParamFilter(name, item.value)
  } else {
    const params: string[] = [...getUrlParams(name), item.value]
    setUrlParams(name, params)
  }

  // Debounce the emit for API calls
  debouncedFilterChangeEmit({ item, name })
}

// Debounced emit function for filter changes (no URL param changes here)
const debouncedFilterChangeEmit = useDebounceFn(
  (payload: FilterChangePayload) => {
    emit('filter-change', payload)
  },
  300,
)

const addUserInput = () => {
  if (!inputText.value.trim()) return

  const values = inputText.value
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v)
  const currentParams = getUrlParams(props.filter.name)
  const newlyAddedValues: string[] = []

  for (const value of values) {
    // Add to our user-added items set immediately
    userAddedItemsSet.value.add(value)

    // Track newly added values that aren't already in params
    if (!currentParams.includes(value)) {
      newlyAddedValues.push(value)
    }
  }

  // Update URL params with all new values at once
  if (newlyAddedValues.length > 0) {
    const newParams = [...currentParams, ...newlyAddedValues]
    setUrlParams(props.filter.name, newParams)

    // Debounce the emit for API calls - emit for each newly added value
    for (const value of newlyAddedValues) {
      debouncedFilterChangeEmit({
        item: { value, label: value, active: true } as FilterItemWithState,
        name: props.filter.name,
      })
    }
  }

  inputText.value = ''
}

const removeUserAddedItem = (value: string) => {
  // Remove from our user-added items set immediately
  userAddedItemsSet.value.delete(value)
  // Remove from URL params immediately for instant UI feedback
  removeUrlParamFilter(props.filter.name, value)

  // Debounce the emit for API calls
  debouncedFilterChangeEmit({
    item: { value, label: value } as FilterItemWithState,
    name: props.filter.name,
  })
}

const selectAllFilters = (paramName: string) => {
  // Update URL params immediately for instant UI feedback
  setUrlParams(paramName, predefinedValues.join('|'))

  // Debounce the emit for API calls
  debouncedSelectAllEmit(paramName)
}

// Debounced emit function for select all
const debouncedSelectAllEmit = useDebounceFn((paramName: string) => {
  emit('filter-all', paramName)
}, 300)

const clearAllFilters = (paramName: string) => {
  emit('filter-clear', paramName)
  deleteUrlParam(paramName)
}

// Initialize userAddedItemsSet with existing user-added items from URL params
const initializeUserAddedItems = () => {
  const currentParams = getUrlParams(props.filter.name)

  for (const value of currentParams) {
    if (!predefinedValues.includes(value)) {
      userAddedItemsSet.value.add(value)
    }
  }
}

// Initialize on component mount
initializeUserAddedItems()

const listContainerRef = useTemplateRef<HTMLElement>('listContainer')

const displayedItems = computed(() =>
  props.search ? filteredItems.value : allItems.value,
)

const virtualizer = useVirtualizer(
  computed(() => ({
    count: displayedItems.value.length,
    estimateSize: () => 36,
    getScrollElement: () => listContainerRef.value,
    overscan: 5,
  })),
)

const virtualItems = computed(() => virtualizer.value.getVirtualItems())
const totalSize = computed(() => virtualizer.value.getTotalSize())
const virtualItemsWithData = computed(() =>
  virtualItems.value.map((vItem) => ({
    ...vItem,
    item: displayedItems.value[vItem.index],
  })),
)

watch(searchInput, () => virtualizer.value.scrollToOffset(0))
</script>

<template>
  <div class="relative inline-block">
    <div ref="activator">
      <slot v-bind="{ open, setOpen }" name="activator">
        <FilterChip
          v-model="activeFilterLabels"
          class="h-full"
          :label="filter.label"
          button-border-radius="rounded-lg"
          @click="setOpen(true)"
        />
      </slot>
    </div>
    <FloatingUi
      v-if="open"
      class="bg-surface shadow-lg"
      :anchor="activator!"
      placement="bottom-start"
      @click-outside="open = false"
    >
      <template #default="{ maxDimensions }">
        <section class="min-w-54 flex flex-col" :style="maxDimensions">
          <header class="mx-3 border-b border-neutral-200">
            <div
              class="flex w-full items-center justify-between space-x-4 py-3"
            >
              <h3 class="h3 whitespace-nowrap text-neutral-900">
                {{ filter.label }}
              </h3>
              <button
                class="rounded-full p-1"
                aria-label="Close"
                data-testid="close-dropdown-button"
                @click="open = false"
              >
                <div class="i-ph-x-bold" />
              </button>
            </div>

            <!-- Add custom input section -->
            <div class="flex items-center gap-2 pb-3">
              <input
                v-model="inputText"
                class="flex-1 rounded-lg border border-neutral-200 px-3 py-2 text-xs"
                placeholder="ex: ID1, ID2, ID3..."
                @keydown.enter="addUserInput"
              />
              <Button size="xs" type="button" @click="addUserInput">Add</Button>
            </div>

            <div v-if="search" class="flex items-center justify-between pb-3">
              <Search
                v-model="searchInput"
                :focus="open"
                placeholder="Search..."
                type="search"
              />
            </div>
          </header>
          <div
            ref="listContainer"
            class="filter-item-container min-h-0 flex-1 overflow-y-auto pb-3 pt-2"
          >
            <ul class="relative px-3" :style="{ height: `${totalSize}px` }">
              <li
                v-for="vData in virtualItemsWithData"
                :key="vData.item.value"
                class="absolute w-full"
                :style="{ transform: `translateY(${vData.start}px)` }"
              >
                <div class="flex min-w-0 items-center justify-between">
                  <ListItemCheckbox
                    class="min-w-0 flex-1 overflow-hidden"
                    :checked="!!vData.item.active"
                    :data-testid="
                      'filter-item-' +
                      vData.item.label.replaceAll(' ', '-').toLowerCase()
                    "
                    @click="filterChange(vData.item, filter.name)"
                  >
                    <span class="block truncate">
                      {{ vData.item.display_name || vData.item.label }}
                    </span>
                  </ListItemCheckbox>

                  <button
                    v-if="vData.item.isUserAdded"
                    class="ml-2 shrink-0 rounded-full p-1 text-neutral-400 hover:bg-red-50 hover:text-red-500"
                    :aria-label="`Remove ${vData.item.label}`"
                    type="button"
                    @click.stop="removeUserAddedItem(vData.item.value)"
                  >
                    <div class="i-ph-x-bold h-3 w-3" />
                  </button>
                </div>
              </li>
            </ul>
          </div>
          <footer
            v-if="!selectAllDisabled || !clearAllDisabled"
            class="mx-3 flex justify-end border-t border-neutral-200 py-3"
          >
            <Button
              v-if="!selectAllDisabled"
              class="px-2 text-sm"
              variant="text"
              @click="selectAllFilters(filter.name)"
            >
              Select all
            </Button>
            <Button
              v-if="!clearAllDisabled"
              class="px-2 text-sm"
              variant="text"
              @click="clearAllFilters(filter.name)"
            >
              Clear
            </Button>
          </footer>
        </section>
      </template>
    </FloatingUi>
  </div>
</template>
